if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        
        // Prestige Dots Init
        this.dotsWrapper = this.querySelector('.slideshow__control-wrapper');
        if (this.dotsWrapper && this.elements.viewer) {
          // Delay init slightly to ensure slider layout is ready
          setTimeout(() => this.initDots(), 50);
          this.elements.viewer.addEventListener('slideChanged', this.updateDots.bind(this));
          
          subscribe(PUB_SUB_EVENTS.variantChange, () => {
            // Need a tiny delay for the DOM update to settle in the browser layout
            setTimeout(() => {
              this.initDots();
              if (this.visibleSlides && this.visibleSlides.length > 0) {
                this.elements.viewer.slider.scrollTo({ left: this.visibleSlides[0].offsetLeft, behavior: 'smooth' });
              }
            }, 50);
          });
        }

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      initDots() {
        if (!this.dotsWrapper || !this.elements.viewer || !this.elements.viewer.slider) return;
        
        this.visibleSlides = Array.from(this.elements.viewer.querySelectorAll('.slider__slide'));

        this.dotsWrapper.innerHTML = '';
        this.visibleSlides.forEach((slide, index) => {
          const btn = document.createElement('button');
          btn.className = 'slider-counter__link slider-counter__link--dots link' + (slide.classList.contains('is-active') || index === 0 ? ' slider-counter__link--active' : '');
          btn.innerHTML = '<span class="dot"></span>';
          
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            this.elements.viewer.slider.scrollTo({
              left: slide.offsetLeft,
              behavior: 'smooth'
            });
          });
          
          this.dotsWrapper.appendChild(btn);
        });
        
        // Fix for multiple active dots issue
        const activeDots = this.dotsWrapper.querySelectorAll('.slider-counter__link--active');
        if (activeDots.length > 1) {
          activeDots.forEach((dot, i) => { if (i !== 0) dot.classList.remove('slider-counter__link--active'); });
        }
      }

      updateDots(event) {
        if (!this.dotsWrapper || !this.visibleSlides) return;
        const currentSlide = event.detail.currentElement;
        const index = this.visibleSlides.indexOf(currentSlide);
        
        const dots = this.dotsWrapper.querySelectorAll('.slider-counter__link');
        dots.forEach(dot => dot.classList.remove('slider-counter__link--active'));
        if (dots[index]) {
          dots[index].classList.add('slider-counter__link--active');
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          const activeMediaRect = activeMedia.getBoundingClientRect();
          const isVertical = this.dataset.desktopLayout === 'stacked' || this.dataset.desktopLayout === 'thumbnail';
          
          if (!this.mql.matches || (this.elements.thumbnails && !isVertical)) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          
          if (isVertical && this.mql.matches) {
            // Subtract 100px to perfectly align with the top: 10rem sticky thumbnails
            const top = activeMediaRect.top + window.scrollY - 100;
            window.scrollTo({ top: top, behavior: 'smooth' });
          } else {
            if (activeMediaRect.top > -0.5) return;
            const top = activeMediaRect.top + window.scrollY;
            window.scrollTo({ top: top, behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        const isVertical = this.dataset.desktopLayout === 'stacked' || this.dataset.desktopLayout === 'thumbnail';
        if (isVertical && this.mql.matches) {
          this.elements.thumbnails.slider.scrollTo({ top: thumbnail.offsetTop, behavior: 'smooth' });
        } else {
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
